/**
 * Review a value a person typed on an earlier version, now that the
 * materials state readings beside it (DOP-664).
 *
 * Opening the edit form here asked the wrong question: the person is not
 * being asked to retype their value, they are being asked whether they
 * still want it. So the three real answers are explicit, in order:
 *  - Keep my value: a fresh entry of the same value, which clears the
 *    "review" flag. It keeps the document the value was entered from, or
 *    its note; only an entry with neither is asked where it came from.
 *  - Use a reading: the materials' readings as the same cards the conflict
 *    chooser shows — the figure, the file, where in it — each with Use.
 *  - Change it: the edit form, as before.
 *
 * The ledger's reason is not printed: it lists every reading in one
 * sentence, including the one the entry was edited from, which the cards
 * leave out (DOP-658), so the sentence and the cards disagreed on the count.
 */

import { ArrowUpRight, FileText } from "lucide-react";
import React, { useId, useState } from "react";

import type {
  DealScreeningFact,
  DealScreeningSetFactPayload,
  DealScreeningSource,
} from "../../types/dealScreening";
import { BTN, BTN_PRIMARY, INPUT, KICKER, KIND_KICKER } from "./checkStyles";
import { placeInFile } from "./ConflictChooser";
import {
  formatFactValue,
  formatIsoDay,
  unconvertedCurrency,
} from "./dealScreeningFormat";
import { fromSavedSource } from "./documentSource";

export interface StaleEntryReviewProps {
  /** The entered fact; its `alternates` are the materials' readings. */
  fact: DealScreeningFact;
  saving: boolean;
  onKeep: (
    fact: DealScreeningFact,
    payload: DealScreeningSetFactPayload,
  ) => void;
  onChoose: (chosen: DealScreeningSource) => void;
  onOpenSource: (source: DealScreeningSource) => void;
  /** Open the edit form instead. */
  onChange: () => void;
}

const StaleEntryReview: React.FC<StaleEntryReviewProps> = ({
  fact,
  saving,
  onKeep,
  onChoose,
  onOpenSource,
  onChange,
}) => {
  const savedNote = (fact.note ?? "").trim();
  const [note, setNote] = useState(savedNote);
  const noteId = useId();
  const name = fact.period ? `${fact.label} ${fact.period}` : fact.label;
  const yours = formatFactValue(
    fact.value,
    fact.unit,
    unconvertedCurrency(fact),
  );
  const linked = fromSavedSource(fact.source);
  const linkedPlace = fact.source ? placeInFile(fact.source) : null;
  const keepReady = !saving && (note.trim() !== "" || linked !== null);
  const keep = () => {
    if (!keepReady) return;
    onKeep(fact, {
      value: fact.value,
      note: note.trim(),
      period: fact.period,
      ...(linked ? { source: linked } : {}),
    });
  };
  const readings = fact.alternates.length;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        keep();
      }}
      aria-label="Review your value"
      className="flex flex-col gap-4"
      data-testid="stale-entry-review"
    >
      <div>
        <p className={`${KICKER} ${KIND_KICKER.stale.color}`}>
          {KIND_KICKER.stale.label}
        </p>
        <h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#111111]">
          Keep your {name}, or use what the materials say?
        </h2>
        <p className="mt-1.5 text-sm text-[#5B6472]">
          {readings === 0
            ? "The materials have changed since you entered this."
            : readings === 1
              ? "The materials now give a reading of their own."
              : `The materials now give ${readings} readings of their own.`}{" "}
          Your value stays in the memo until you decide.
        </p>
      </div>

      <div className="rounded-xl border-2 border-[#004C4B] bg-white p-4">
        <div className="flex min-h-[22px] items-center gap-2">
          <span className="inline-flex h-[22px] items-center rounded-full bg-[#ecfdf3] px-2 text-[11px] font-semibold text-[#067647]">
            Your value
          </span>
          <span className="text-xs text-[#6B7280]">
            {fact.entered_by_name ? `${fact.entered_by_name}, ` : ""}
            {fact.entered_at
              ? formatIsoDay(fact.entered_at)
              : "entered earlier"}
          </span>
        </div>
        <p className="mt-2 text-xl font-bold leading-snug tabular-nums text-[#111111]">
          {yours}
        </p>
        {linked && fact.source ? (
          <p
            className="mt-1.5 text-[13px] text-[#3f4247]"
            data-testid="stale-linked-source"
          >
            From: {fact.source.title || fact.source.filename}
            {linkedPlace ? `, ${linkedPlace}` : ""}
            {savedNote ? ` · ${savedNote}` : ""}{" "}
            <button
              type="button"
              onClick={() => fact.source && onOpenSource(fact.source)}
              className="ml-1 inline-flex items-center gap-1 font-semibold text-[#004C4B] hover:underline"
            >
              See it in the file
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </p>
        ) : savedNote ? (
          <p className="mt-1.5 text-[13px] text-[#3f4247]">From: {savedNote}</p>
        ) : (
          <div className="mt-3">
            <label
              htmlFor={noteId}
              className="text-[12.5px] font-semibold text-[#3f4247]"
            >
              Where it's from
            </label>
            <input
              id={noteId}
              className={`${INPUT} mt-1`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. Call with the CFO, Sep 22"
            />
            <p className="mt-1 text-xs text-[#b54708]">
              Your value has no source on file. Say where it's from to keep it.
            </p>
          </div>
        )}
        <button
          type="submit"
          disabled={!keepReady}
          className={`${BTN_PRIMARY} mt-3 h-10`}
          data-testid="stale-keep"
        >
          {saving ? "Saving…" : "Keep my value"}
        </button>
      </div>

      {readings > 0 && (
        <div>
          <p className="text-[12.5px] font-semibold text-[#3f4247]">
            What the materials say
          </p>
          <div className="mt-2 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            {fact.alternates.map((option) => {
              const label = formatFactValue(option.value, option.unit);
              const place = placeInFile(option.source);
              return (
                <div
                  key={option.source.anchor}
                  data-testid="stale-reading"
                  className="flex flex-col gap-3 rounded-xl border border-[#e5e7eb] bg-white p-4"
                >
                  <p className="text-xl font-bold leading-snug tabular-nums text-[#111111]">
                    {label}
                  </p>
                  <div className="text-[13px] leading-snug">
                    <p className="flex min-w-0 items-center gap-1.5 text-[#3f4247]">
                      <FileText
                        className="h-3.5 w-3.5 shrink-0 text-[#6B7280]"
                        aria-hidden
                      />
                      <span
                        className="truncate"
                        title={option.source.title || option.source.filename}
                      >
                        {option.source.title || option.source.filename}
                      </span>
                    </p>
                    <p className="mt-0.5 pl-5 text-[12.5px] text-[#6B7280]">
                      {[
                        place,
                        option.source.doc_date
                          ? `dated ${formatIsoDay(option.source.doc_date)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <button
                      type="button"
                      onClick={() => onOpenSource(option.source)}
                      className="mt-1.5 inline-flex items-center gap-1 pl-5 text-[13px] font-semibold text-[#004C4B] hover:underline"
                    >
                      See it in the file
                      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                  <button
                    type="button"
                    aria-label={`Use ${label}`}
                    disabled={saving}
                    onClick={() => onChoose(option.source)}
                    className={`${BTN} mt-auto h-10`}
                  >
                    {saving
                      ? "Saving…"
                      : option.unit === "text"
                        ? "Use this reading"
                        : `Use ${label}`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-dashed border-[#d1d5db] px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#111111]">
              Neither as it stands?
            </p>
            <p className="mt-0.5 text-[13px] leading-normal text-[#5B6472]">
              Change the value or where it came from; the memo prints the new
              answer as entered by you.
            </p>
          </div>
          <button
            type="button"
            onClick={onChange}
            className={BTN}
            disabled={saving}
          >
            Change it
          </button>
        </div>
      </div>
    </form>
  );
};

export default StaleEntryReview;
